import { baseApi } from "./api";
import type { Review, ReviewPayload, ReviewUpdate, ReviewStats } from "@nicsmith/shared_types";

export const reviewsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all reviews (admin only)
    getAllReviews: builder.query<Review[], { limit?: number }>({
      query: ({ limit = 50 }) => ({
        url: "/reviews",
        params: { limit },
      }),
      providesTags: ["Reviews"],
    }),

    // Get reviews for a specific course
    getCourseReviews: builder.query<Review[], { courseId: number; limit?: number; approvedOnly?: boolean }>({
      query: ({ courseId, limit = 20, approvedOnly = true }) => ({
        url: `/reviews/course/${courseId}`,
        params: { limit, approvedOnly },
      }),
      providesTags: (_result, _error, { courseId }) => [{ type: "Reviews", id: courseId }],
    }),

    // Get review stats for a course
    getCourseReviewStats: builder.query<ReviewStats, number>({
      query: (courseId) => `/reviews/course/${courseId}/stats`,
      providesTags: (_result, _error, courseId) => [{ type: "Reviews", id: `stats-${courseId}` }],
    }),

    // Get current user's reviews
    getMyReviews: builder.query<Review[], void>({
      query: () => "/reviews/my",
      providesTags: ["MyReviews"],
    }),

    // Create a new review
    createReview: builder.mutation<Review, ReviewPayload>({
      query: (payload) => ({
        url: "/reviews",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: (_result, _error, { course_id }) => [
        "Reviews",
        "MyReviews",
        { type: "Reviews", id: course_id },
        { type: "Reviews", id: `stats-${course_id}` },
        "AdminStats",
      ],
    }),

    // Update a review
    updateReview: builder.mutation<Review, { id: number; updates: ReviewUpdate }>({
      query: ({ id, updates }) => ({
        url: `/reviews/${id}`,
        method: "PUT",
        body: updates,
      }),
      invalidatesTags: ["Reviews", "MyReviews", "AdminStats"],
    }),

    // Delete a review
    deleteReview: builder.mutation<void, number>({
      query: (id) => ({
        url: `/reviews/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Reviews", "MyReviews", "AdminStats"],
    }),

    // Moderate a review (admin only)
    moderateReview: builder.mutation<Review, { id: number; is_approved: boolean }>({
      query: ({ id, is_approved }) => ({
        url: `/reviews/${id}/moderate`,
        method: "PATCH",
        body: { is_approved },
      }),
      invalidatesTags: ["Reviews", "AdminStats"],
    }),
  }),
  overrideExisting: true,
});

export const {
  useGetAllReviewsQuery,
  useGetCourseReviewsQuery,
  useGetCourseReviewStatsQuery,
  useGetMyReviewsQuery,
  useCreateReviewMutation,
  useUpdateReviewMutation,
  useDeleteReviewMutation,
  useModerateReviewMutation,
} = reviewsApi;
